import {
    Component,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import type {
    ImageCropAspectRatioRange,
    ImageDimensions,
} from '@app/features/shared/components/image-upload/image-upload.component'

type CropBox = ImageDimensions & {
    x: number,
    y: number,
}
type CropDragMode = 'move' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw'
type CropDrag = {
    mode: CropDragMode,
    startClientX: number,
    startClientY: number,
    startCrop: CropBox,
}

@Component({
    selector: 'app-image-crop-modal',
    templateUrl: './image-crop-modal.component.html',
    styleUrl: './image-crop-modal.component.sass',
})
export class ImageCropModalComponent implements OnInit, OnDestroy {

    @Input() file!: File
    @Input() cropDimensions!: ImageDimensions
    @Input() aspectRatioRange?: ImageCropAspectRatioRange
    @ViewChild('image') image?: ElementRef<HTMLImageElement>

    protected imageUrl?: string
    protected imageDimensions?: ImageDimensions
    protected displayDimensions?: ImageDimensions
    protected crop?: CropBox
    protected drag?: CropDrag

    constructor(
        public activeModal: NgbActiveModal
    ) {
    }

    ngOnInit() {
        this.imageUrl = URL.createObjectURL(this.file)
    }

    ngOnDestroy() {
        if (this.imageUrl) {
            URL.revokeObjectURL(this.imageUrl)
        }
    }

    protected onImageLoaded(event: Event) {
        const image = event.target as HTMLImageElement
        this.imageDimensions = {
            width: image.naturalWidth,
            height: image.naturalHeight,
        }
        this.updateDisplayedImageDimensions()
        this.crop = undefined
        this.crop = this.initialCrop()
    }

    protected startDrag(event: PointerEvent, mode: CropDragMode) {
        if (!this.crop) {
            return
        }
        event.preventDefault()
        event.stopPropagation()
        this.drag = {
            mode,
            startClientX: event.clientX,
            startClientY: event.clientY,
            startCrop: { ...this.crop },
        }
    }

    @HostListener('document:pointermove', ['$event'])
    protected onPointerMove(event: PointerEvent) {
        if (!this.drag) {
            return
        }
        event.preventDefault()
        const deltaX = (event.clientX - this.drag.startClientX) / this.scaleX()
        const deltaY = (event.clientY - this.drag.startClientY) / this.scaleY()
        if (this.drag.mode === 'move') {
            this.moveCrop(deltaX, deltaY)
            return
        }
        this.resizeCrop(deltaX, deltaY)
    }

    @HostListener('document:pointerup')
    protected onPointerUp() {
        this.drag = undefined
    }

    @HostListener('window:resize')
    protected updateDisplayedImageDimensions() {
        const image = this.image?.nativeElement
        if (!image) {
            return
        }
        this.displayDimensions = {
            width: image.clientWidth,
            height: image.clientHeight,
        }
    }

    protected async save() {
        if (!this.crop || !this.image?.nativeElement) {
            return
        }
        const crop = {
            x: Math.round(this.crop.x),
            y: Math.round(this.crop.y),
            width: Math.round(this.crop.width),
            height: Math.round(this.crop.height),
        }
        const canvas = document.createElement('canvas')
        canvas.width = crop.width
        canvas.height = crop.height
        canvas.getContext('2d')?.drawImage(
            this.image.nativeElement,
            crop.x,
            crop.y,
            crop.width,
            crop.height,
            0,
            0,
            crop.width,
            crop.height
        )
        const type = ['image/jpeg', 'image/png', 'image/webp'].includes(this.file.type)
            ? this.file.type
            : 'image/jpeg'
        const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, type, 0.92))
        if (!blob) {
            return
        }
        this.activeModal.close(new File([blob], this.croppedFileName(), { type: blob.type || type }))
    }

    protected get cropLeft() {
        return (this.crop?.x ?? 0) * this.scaleX()
    }

    protected get cropTop() {
        return (this.crop?.y ?? 0) * this.scaleY()
    }

    protected get cropWidth() {
        return (this.crop?.width ?? 0) * this.scaleX()
    }

    protected get cropHeight() {
        return (this.crop?.height ?? 0) * this.scaleY()
    }

    protected get cropSizeLabel() {
        if (!this.crop) {
            return ''
        }
        return `${Math.round(this.crop.width)} x ${Math.round(this.crop.height)} px`
    }

    protected get minAspectRatio() {
        return this.aspectRatioRange?.min ?? this.initialAspectRatio()
    }

    protected get maxAspectRatio() {
        return this.aspectRatioRange?.max ?? this.initialAspectRatio()
    }

    protected get aspectRatioLabel() {
        return this.formatAspectRatio(this.aspectRatio())
    }

    protected get minAspectRatioLabel() {
        return this.formatAspectRatio(this.minAspectRatio)
    }

    protected get maxAspectRatioLabel() {
        return this.formatAspectRatio(this.maxAspectRatio)
    }

    protected get minimumCropSizeLabel() {
        const minimum = this.minimumCropDimensions()
        return `${Math.ceil(minimum.width)} x ${Math.ceil(minimum.height)} px`
    }

    protected get cropMeetsMinimum() {
        if (!this.crop) {
            return false
        }
        const tolerance = 0.5
        return this.crop.width + tolerance >= this.cropDimensions.width &&
            this.crop.height + tolerance >= this.cropDimensions.height
    }

    private initialCrop(): CropBox {
        const image = this.imageDimensions!
        const ratio = this.aspectRatio()
        let width = image.width
        let height = width / ratio
        if (height > image.height) {
            height = image.height
            width = height * ratio
        }
        return {
            x: (image.width - width) / 2,
            y: (image.height - height) / 2,
            width,
            height,
        }
    }

    private moveCrop(deltaX: number, deltaY: number) {
        const { startCrop } = this.drag!
        const image = this.imageDimensions!
        this.crop = {
            ...startCrop,
            x: this.clamp(startCrop.x + deltaX, 0, image.width - startCrop.width),
            y: this.clamp(startCrop.y + deltaY, 0, image.height - startCrop.height),
        }
    }

    private resizeCrop(deltaX: number, deltaY: number) {
        const { mode, startCrop } = this.drag!
        const image = this.imageDimensions!
        const resizesWidth = mode.includes('e') || mode.includes('w')
        const resizesHeight = mode.includes('n') || mode.includes('s')
        const requestedWidth = resizesWidth
            ? startCrop.width + (mode.includes('w') ? -deltaX : deltaX)
            : startCrop.width
        const requestedHeight = resizesHeight
            ? startCrop.height + (mode.includes('n') ? -deltaY : deltaY)
            : startCrop.height
        const { width, height } = this.resizedDimensions(
            requestedWidth,
            requestedHeight,
            resizesWidth,
            resizesHeight
        )
        const right = startCrop.x + startCrop.width
        const bottom = startCrop.y + startCrop.height
        this.crop = {
            x: this.clamp(
                mode.includes('w') ? right - width : startCrop.x,
                0,
                image.width - width
            ),
            y: this.clamp(
                mode.includes('n') ? bottom - height : startCrop.y,
                0,
                image.height - height
            ),
            width,
            height,
        }
    }

    private resizedDimensions(
        requestedWidth: number,
        requestedHeight: number,
        resizesWidth: boolean,
        resizesHeight: boolean
    ): ImageDimensions {
        const image = this.imageDimensions!
        const limits = this.resizeAspectRatioLimits()
        let width = this.clamp(
            requestedWidth,
            this.cropDimensions.width,
            image.width
        )
        let height = this.clamp(
            requestedHeight,
            this.cropDimensions.height,
            image.height
        )

        if (resizesWidth && !resizesHeight) {
            width = this.clamp(width, height * limits.min, height * limits.max)
            return { width, height }
        }
        if (resizesHeight && !resizesWidth) {
            height = this.clamp(height, width / limits.max, width / limits.min)
            return { width, height }
        }

        const ratio = width / height
        if (ratio < limits.min) {
            return this.projectDimensionsToAspectRatio(width, height, limits.min)
        }
        if (ratio > limits.max) {
            return this.projectDimensionsToAspectRatio(width, height, limits.max)
        }
        return { width, height }
    }

    private resizeAspectRatioLimits() {
        const image = this.imageDimensions!
        return {
            min: Math.max(this.minAspectRatio, this.cropDimensions.width / image.height),
            max: Math.min(this.maxAspectRatio, image.width / this.cropDimensions.height),
        }
    }

    private projectDimensionsToAspectRatio(
        width: number,
        height: number,
        aspectRatio: number
    ): ImageDimensions {
        const image = this.imageDimensions!
        const projectedHeight = (aspectRatio * width + height) /
            (aspectRatio * aspectRatio + 1)
        const minHeight = Math.max(
            this.cropDimensions.height,
            this.cropDimensions.width / aspectRatio
        )
        const maxHeight = Math.min(image.height, image.width / aspectRatio)
        const constrainedHeight = this.clamp(projectedHeight, minHeight, maxHeight)
        return {
            width: constrainedHeight * aspectRatio,
            height: constrainedHeight,
        }
    }

    private aspectRatio() {
        return this.crop
            ? this.crop.width / this.crop.height
            : this.initialAspectRatio()
    }

    private initialAspectRatio() {
        const fixedAspectRatio = this.cropDimensions.width / this.cropDimensions.height
        if (!this.aspectRatioRange) {
            return fixedAspectRatio
        }
        return this.clamp(
            this.aspectRatioRange.initial,
            this.aspectRatioRange.min,
            this.aspectRatioRange.max
        )
    }

    private minimumCropDimensions(): ImageDimensions {
        const ratio = this.aspectRatio()
        const width = Math.max(this.cropDimensions.width, this.cropDimensions.height * ratio)
        return {
            width,
            height: width / ratio,
        }
    }

    private formatAspectRatio(ratio: number) {
        const commonRatios = [
            { ratio: 1, label: '1:1' },
            { ratio: 4 / 3, label: '4:3' },
            { ratio: 3 / 2, label: '3:2' },
            { ratio: 16 / 9, label: '16:9' },
        ]
        const commonRatio = commonRatios.find(candidate =>
            Math.abs(candidate.ratio - ratio) < 0.005
        )
        return commonRatio?.label ?? `${ratio.toFixed(2)}:1`
    }

    private scaleX() {
        return this.displayDimensions && this.imageDimensions
            ? this.displayDimensions.width / this.imageDimensions.width
            : 1
    }

    private scaleY() {
        return this.displayDimensions && this.imageDimensions
            ? this.displayDimensions.height / this.imageDimensions.height
            : 1
    }

    private clamp(value: number, min: number, max: number) {
        return Math.max(min, Math.min(max, value))
    }

    private croppedFileName() {
        const dotIndex = this.file.name.lastIndexOf('.')
        if (dotIndex < 1) {
            return `${this.file.name}-cropped`
        }
        return `${this.file.name.substring(0, dotIndex)}-cropped${this.file.name.substring(dotIndex)}`
    }
}
